/**
 * API Service - จัดการการเรียกใช้ API และข้อมูล
 * เชื่อมต่อกับ Backend API Server โดยตรง
 */

class ApiService {
  constructor() {
    // ใช้ relative path สำหรับ monolithic architecture
    this.baseURL = window.location.origin;
  }

  // ==================== User Management ====================

  // ดึงข้อมูลผู้ใช้ทั้งหมด
  async getUsers() {
    try {
      const response = await fetch(`${this.baseURL}/api/users`, {
        headers: {
          'Accept': 'application/json; charset=utf-8',
          'Accept-Charset': 'utf-8'
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { success: false, error: error.message };
    }
  }

  // ดึงข้อมูลผู้ใช้ตาม ID
  async getUserById(id) {
    try {
      const response = await fetch(`${this.baseURL}/api/users/${id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching user:', error);
      return { success: false, error: error.message };
    }
  }

  // สร้างผู้ใช้ใหม่
  async createUser(userData) {
    try {
      const response = await fetch(`${this.baseURL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Accept': 'application/json; charset=utf-8',
          'Accept-Charset': 'utf-8'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error creating user:', error);
      return { success: false, error: error.message };
    }
  }

  // อัปเดตข้อมูลผู้ใช้
  async updateUser(id, userData) {
    try {
      const response = await fetch(`${this.baseURL}/api/users/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Accept': 'application/json; charset=utf-8',
          'Accept-Charset': 'utf-8'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบผู้ใช้
  async deleteUser(id) {
    try {
      const response = await fetch(`${this.baseURL}/api/users/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.message };
    }
  }

  // ตรวจสอบการเข้าสู่ระบบ
  async login(username, password) {
    try {
      const response = await fetch(`${this.baseURL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Error during login:', error);
      // ตรวจสอบว่าเป็น network error หรือไม่
      if (error.message === 'Failed to fetch') {
        return { success: false, error: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบว่าเซิร์ฟเวอร์กำลังทำงานอยู่' };
      }
      return { success: false, error: error.message };
    }
  }

  // ==================== Battery Stock Management ====================

  // ดึงข้อมูลสต๊อกแบตเตอรี่
  async getBatteryStock(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.status && filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }
      if (filters.brand) {
        queryParams.append('brand', filters.brand);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }

      const url = `${this.baseURL}/api/battery-stock${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching battery stock:', error);
      return { success: false, error: error.message };
    }
  }

  // เพิ่มสต๊อกแบตเตอรี่ใหม่
  async createBatteryStock(stockData, shouldOverwrite = false) {
    try {
      const url = shouldOverwrite
        ? `${this.baseURL}/api/battery-stock/upsert`
        : `${this.baseURL}/api/battery-stock`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stockData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Return the specific error message from server
        return { success: false, error: data.error || `HTTP error! status: ${response.status}` };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating battery stock:', error);
      return { success: false, error: error.message };
    }
  }

  // อัปเดตสต๊อกแบตเตอรี่
  async updateBatteryStock(id, stockData) {
    try {
      const response = await fetch(`${this.baseURL}/api/battery-stock/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stockData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error updating battery stock:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบสต๊อกแบตเตอรี่
  async deleteBatteryStock(id) {
    try {
      const response = await fetch(`${this.baseURL}/api/battery-stock/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting battery stock:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== Charger Stock Management ====================

  // ดึงข้อมูลสต๊อกชาร์จเจอร์
  async getChargerStock(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.status && filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }
      if (filters.brand) {
        queryParams.append('brand', filters.brand);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }

      const url = `${this.baseURL}/api/charger-stock${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching charger stock:', error);
      return { success: false, error: error.message };
    }
  }

  // เพิ่มสต๊อกชาร์จเจอร์ใหม่
  async createChargerStock(stockData, shouldOverwrite = false) {
    try {
      const url = shouldOverwrite
        ? `${this.baseURL}/api/charger-stock/upsert`
        : `${this.baseURL}/api/charger-stock`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stockData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Return the specific error message from server
        return { success: false, error: data.error || `HTTP error! status: ${response.status}` };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating charger stock:', error);
      return { success: false, error: error.message };
    }
  }

  // อัปเดตสต๊อกชาร์จเจอร์
  async updateChargerStock(id, stockData) {
    try {
      const response = await fetch(`${this.baseURL}/api/charger-stock/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stockData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error updating charger stock:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบสต๊อกชาร์จเจอร์
  async deleteChargerStock(id) {
    try {
      const response = await fetch(`${this.baseURL}/api/charger-stock/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting charger stock:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== Customer Management ====================

  // ดึงข้อมูลลูกค้าทั้งหมด
  async getCustomers() {
    try {
      const response = await fetch(`${this.baseURL}/api/customers`, {
        headers: {
          'Accept': 'application/json; charset=utf-8',
          'Accept-Charset': 'utf-8'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching customers:', error);
      return { success: false, error: error.message };
    }
  }

  // สร้างลูกค้าใหม่
  async createCustomer(customerData) {
    try {
      const response = await fetch(`${this.baseURL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Accept': 'application/json; charset=utf-8',
          'Accept-Charset': 'utf-8'
        },
        body: JSON.stringify(customerData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error creating customer:', error);
      return { success: false, error: error.message };
    }
  }

  // อัปเดตข้อมูลลูกค้า
  async updateCustomer(id, customerData) {
    try {
      const response = await fetch(`${this.baseURL}/api/customers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error updating customer:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบลูกค้า
  async deleteCustomer(id) {
    try {
      const response = await fetch(`${this.baseURL}/api/customers/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting customer:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== Sales Management ====================

  // ดึงข้อมูลการขายทั้งหมด
  async getSales() {
    try {
      console.log('ApiService: Fetching sales from:', `${this.baseURL}/api/sales`);
      const response = await fetch(`${this.baseURL}/api/sales`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('ApiService: Sales data received:', data);
      return { success: true, data };
    } catch (error) {
      console.error('ApiService: Error fetching sales:', error);
      return { success: false, error: error.message };
    }
  }

  // ดึงข้อมูลการขายแต่ละรายการ
  async getSale(id) {
    try {
      console.log('ApiService: Fetching sale from:', `${this.baseURL}/api/sales/${id}`);
      const response = await fetch(`${this.baseURL}/api/sales/${id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('ApiService: Sale data received:', data);
      return { success: true, data };
    } catch (error) {
      console.error('ApiService: Error fetching sale:', error);
      return { success: false, error: error.message };
    }
  }

  // บันทึกการขาย
  async createSale(saleData) {
    try {
      console.log('ApiService: Creating sale with data:', saleData);
      const response = await fetch(`${this.baseURL}/api/sales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saleData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('ApiService: Sale created successfully:', data);
      return { success: true, data };
    } catch (error) {
      console.error('ApiService: Error creating sale:', error);
      return { success: false, error: error.message };
    }
  }

  // อัปเดตข้อมูลการขาย
  async updateSale(id, saleData) {
    try {
      const response = await fetch(`${this.baseURL}/api/sales/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saleData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error updating sale:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบข้อมูลการขาย
  async deleteSale(id) {
    try {
      const response = await fetch(`${this.baseURL}/api/sales/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting sale:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบข้อมูลการขายแบบ Force (พร้อมลบตาราง PM และการตรวจสภาพที่เกี่ยวข้อง)
  // ลบข้อมูลการขายแบบ Force (พร้อมลบตาราง PM และการตรวจสภาพที่เกี่ยวข้อง)
  async forceDeleteSale(id, reason = '', deletedBy = 'Admin') {
    try {
      const queryParams = new URLSearchParams({
        force: 'true',
        reason: reason,
        deleted_by: deletedBy
      });

      const response = await fetch(`${this.baseURL}/api/sales/${id}?${queryParams.toString()}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error force deleting sale:', error);
      return { success: false, error: error.message };
    }
  }

  // ดึงประวัติการลบข้อมูล
  async getDeletionLogs() {
    try {
      const response = await fetch(`${this.baseURL}/api/deletion-logs`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching deletion logs:', error);
      return { success: false, error: error.message };
    }
  }

  // ดึง PM schedules ตาม serial number
  async getSchedulesBySerial(serial, status = 'all') {
    try {
      const params = new URLSearchParams({ status, serial });
      const response = await fetch(`${this.baseURL}/api/pm-service/schedules?${params.toString()}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching schedules by serial:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบ PM schedule ตาม ID (รองรับ cascade)
  async deleteSchedule(id, { cascade = false } = {}) {
    try {
      const url = `${this.baseURL}/api/pm-service/schedules/${id}${cascade ? '?cascade=true' : ''}`;
      const response = await fetch(url, { method: 'DELETE' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting PM schedule:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบ PM schedules ทั้งหมดของ serial (ใช้ cascade)
  async deleteSchedulesBySerial(serial) {
    try {
      const list = await this.getSchedulesBySerial(serial, 'all');
      if (!list.success) throw new Error(list.error || 'ไม่สามารถดึงรายการ PM schedules ได้');

      const schedules = Array.isArray(list.data) ? list.data : (list.data.schedules || []);
      let deleted = 0;
      for (const sched of schedules) {
        const res = await this.deleteSchedule(sched.id, { cascade: true });
        if (res.success) deleted += 1;
      }

      return { success: true, deleted };
    } catch (error) {
      console.error('Error deleting schedules by serial:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== PM Inspection Management ====================
  // อัปเดตข้อมูลการตรวจสภาพ (PM inspection)
  async updateInspection(id, inspectionData) {
    try {
      const response = await fetch(`${this.baseURL}/api/pm-service/inspections/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inspectionData)
      });

      const resBody = await response.json();
      if (!response.ok) {
        return { success: false, error: resBody.error || `HTTP error! status: ${response.status}` };
      }
      return { success: true, data: resBody };
    } catch (error) {
      console.error('Error updating inspection:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== Quotation Management ====================

  // ดึงข้อมูลใบเสนอราคาทั้งหมด
  async getQuotations() {
    console.log('=== ApiService.getQuotations called ===');
    console.log('Fetching from URL:', `${this.baseURL}/api/quotations`);
    try {
      const response = await fetch(`${this.baseURL}/api/quotations`);
      console.log('Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Response data:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching quotations:', error);
      return { success: false, error: error.message };
    }
  }

  // สร้างใบเสนอราคาใหม่
  async createQuotation(quotationData) {
    console.log('=== ApiService.createQuotation called ===', quotationData);
    try {
      console.log('=== Sending POST request to ===', `${this.baseURL}/api/quotations`);
      const response = await fetch(`${this.baseURL}/api/quotations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quotationData),
      });

      console.log('=== Response status ===', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('=== API Error Response ===', errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('=== API Success Response ===', data);
      return { success: true, data };
    } catch (error) {
      console.error('=== ApiService.createQuotation error ===', error);
      return { success: false, error: error.message };
    }
  }

  // อัปเดตใบเสนอราคา
  async updateQuotation(id, quotationData) {
    try {
      const response = await fetch(`${this.baseURL}/api/quotations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quotationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error updating quotation:', error);
      return { success: false, error: error.message };
    }
  }

  // ลบใบเสนอราคา
  async deleteQuotation(id) {
    try {
      const response = await fetch(`${this.baseURL}/api/quotations/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting quotation:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== Dashboard Stats ====================

  // ดึงสถิติสำหรับแดชบอร์ด
  async getDashboardStats() {
    try {
      const response = await fetch(`${this.baseURL}/api/dashboard/stats`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== Backup ====================

  // สำรองข้อมูล
  async backupData() {
    try {
      const response = await fetch(`${this.baseURL}/api/backup`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return { success: true, data: result };
    } catch (error) {
      console.error('Error creating backup:', error);
      return { success: false, error: error.message };
    }
  }

  // กู้คืนข้อมูล
  async restoreData(file) {
    try {
      const formData = new FormData();
      formData.append('backup', file);

      const response = await fetch(`${this.baseURL}/api/restore`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return { success: true, data: result };
    } catch (error) {
      console.error('Error restoring data:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== System Management ====================

  // เริ่มต้นระบบ
  async initializeDatabase() {
    try {
      const response = await fetch(`${this.baseURL}/api/initialize`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return { success: true, data: result };
    } catch (error) {
      console.error('Error initializing system:', error);
      return { success: false, error: error.message };
    }
  }

  // ตรวจสอบสถานะระบบ
  async getDatabaseStatus() {
    try {
      const response = await fetch(`${this.baseURL}/api/status`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.status || 'connected';
    } catch (error) {
      console.error('Error checking database status:', error);
      return 'disconnected';
    }
  }
}

const apiService = new ApiService();

export default apiService;